import { memo, useState, useCallback } from "react";
import { useSelection } from "../contexts/SelectionContext";
import { Card } from "../types";
import {
  Circle,
  CheckCircle,
  Sparkles,
  Info,
  Gauge,
  MapPin,
} from "lucide-react";
import { ListImageWithFallback } from "./ListImageWithFallback";
import { formatPrice } from "@/features/vision-360/utils/formatPrice";
import { getScoreIndicator } from "@/features/vision-360/utils/getScoreIndicator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScoreDetails, ScoreDetailsModal } from "./score-details-modal";
import { StatusBedge } from "@/layouts/page-container/shared/status-bedge";
import {
  getCardUpdateDisplay,
  hasCardUpdateInfo,
} from "@/features/vision-360/utils/card-update-display";

interface Vision360CardProps {
  card: Card;
  onOpenDrawer: (card: Card) => void;
  showSelectButton?: boolean;
  onCenterOnMap?: (card: Card) => void;
  showCenterOnMapButton?: boolean;
}

const calculatePositions = (totalArea: number) => {
  return Math.floor(totalArea / 5);
};

export const Vision360Card = memo(function Vision360Card({
  card,
  onOpenDrawer,
  showSelectButton = true,
  onCenterOnMap,
  showCenterOnMapButton = false,
}: Vision360CardProps) {
  const [scoreDetails, setScoreDetails] = useState<ScoreDetails | null>(null);
  const { isSelected, toggleSelection } = useSelection();
  const selected = isSelected(card.id);

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleSelection(card.id);
  };

  const handleCenterOnMap = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onCenterOnMap && card.coords?.lat && card.coords?.lng) {
        onCenterOnMap(card);
      }
    },
    [onCenterOnMap, card],
  );

  const scoreConfig = getScoreIndicator(card.match_percentage);
  const HIDE_BTS_ITEMS = true;

  const getSourceName = (source: string) => {
    if (source === "vip-office") {
      return "VIP Office";
    }
    return source;
  };

  return (
    <TooltipProvider>
      <div className="border rounded-lg bg-white shadow-sm overflow-hidden cursor-pointer group transition hover:shadow-md flex flex-col h-full relative">
        <div className="relative h-40 w-full flex-shrink-0">
          <div className="absolute top-2 left-2 z-10 flex gap-2">
            {showCenterOnMapButton &&
              onCenterOnMap &&
              card.coords?.lat &&
              card.coords?.lng && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleCenterOnMap}
                      className="bg-[#F9E10D] rounded-md p-1 shadow-sm group-hover:scale-105 transition cursor-pointer"
                      aria-label="Centralizar no mapa"
                    >
                      <MapPin className="h-4 w-4 text-gray-600" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-black text-white">
                    Clique para centralizar no mapa
                  </TooltipContent>
                </Tooltip>
              )}
            {card.origin === "integration" && (
              <div className="bg-[#181D25] rounded-md px-2 py-1 shadow-sm text-xs font-bold text-white">
                {`${getSourceName(card?.source || "")}`}
              </div>
            )}
            {card.score && (
              <Tooltip>
                <TooltipTrigger
                  asChild
                  onClick={() =>
                    setScoreDetails({
                      scoreDetails: card.score ?? [],
                      details: card.score_details,
                    })
                  }
                >
                  <div
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold"
                    style={{ backgroundColor: scoreConfig, color: "#222" }}
                  >
                    <Gauge className="h-4 w-4" style={{ color: "#222" }} />
                    <span>{Number(card.match_percentage) || 0}%</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-black text-white">
                  Clique para ver mais detalhes do score
                </TooltipContent>
              </Tooltip>
            )}
            <StatusBedge
              status={card?.status || 0}
              type="STATUS_PRIVATE_OFFICE"
            />
            {card.recommendation_ai && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold"
                    style={{ background: "#F9E10D", color: "#222" }}
                  >
                    <Sparkles className="h-4 w-4" style={{ color: "#222" }} />
                    <span>Recomendado</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs p-3 bg-white border border-gray-200 shadow-lg"
                >
                  <div className="text-sm text-gray-800 leading-relaxed">
                    <div className="font-semibold mb-1">
                      Recomendação Vision360
                    </div>
                    <div>{card.recommendation_ai}</div>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div onClick={() => onOpenDrawer(card)} className="w-full h-full">
            <ListImageWithFallback
              key={`image-${card.id}`}
              src={card.images?.[0]}
              alt={card.title}
              className="object-cover w-full h-full"
            />
          </div>

          {showSelectButton && (
            <button
              onClick={handleSelect}
              className="absolute top-2 right-2 bg-white/80 rounded-full p-1 shadow group-hover:scale-110 transition cursor-pointer"
              aria-label={selected ? "Desmarcar" : "Selecionar"}
            >
              {selected ? (
                <CheckCircle className="h-6 w-6" />
              ) : (
                <Circle className="h-6 w-6 text-gray-400" />
              )}
            </button>
          )}
        </div>
        <div
          className="p-4 flex flex-col flex-1 min-h-0"
          onClick={() => onOpenDrawer(card)}
        >
          <div className="font-bold text-lg truncate" title={card.title}>
            {card.title}
          </div>
          <div
            className="text-sm text-gray-900 font-medium mb-3 truncate"
            title={card.space.name}
          >
            {card.space.name}
          </div>
          <div
            className="text-sm text-gray-500 mb-2 truncate"
            title={card.address}
          >
            {card.address}
          </div>
          {!HIDE_BTS_ITEMS && (
            <div className="flex flex-col gap-1 mb-2">
              <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                <span
                  title={`Aluguel: ${formatPrice(card?.pricing?.base_rent_per_sqm)}`}
                >
                  Aluguel:{" "}
                  {formatPrice(card?.pricing?.base_rent_per_sqm, "/m²")}
                </span>
                <span
                  title={`Condomínio: ${formatPrice(card?.pricing?.condo_fee_per_sqm)}`}
                >
                  Condomínio:{" "}
                  {formatPrice(card?.pricing?.condo_fee_per_sqm, "/m²")}
                </span>
                <span
                  title={`IPTU: ${formatPrice(card?.pricing?.iptu_per_sqm)}`}
                >
                  IPTU: {formatPrice(card?.pricing?.iptu_per_sqm, "/m²")}
                </span>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-4 text-xs text-gray-700 mb-1">
            <span
              className="truncate font-medium"
              title={`Capacidade: ${card?.capacity} ${card?.capacity > 1 ? "pessoas" : "pessoa"}`}
            >
              <span className="font-semibold">Capacidade:</span>{" "}
              {card?.capacity} {card?.capacity > 1 ? "pessoas" : "pessoa"}
            </span>
            <span
              className="truncate font-medium"
              title={`Tamanho: ${card?.features?.total_area}m²`}
            >
              <span className="font-semibold">Tamanho:</span>{" "}
              {`${card?.size ? card.size + "m² + área comum" : "Sob consulta"}`}
            </span>

            {!HIDE_BTS_ITEMS && card?.features?.total_area && (
              <div className="flex items-center gap-1">
                <span className="truncate">
                  Posições: {calculatePositions(card?.features?.total_area)}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-black text-white">
                    Consideram-se 5m² por posição
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-gray-700">
            <span
              className="truncate font-medium"
              title={`Créditos: ${card?.credits ? Math.ceil(card?.credits || 0) : "Sob consulta"}`}
            >
              <span className="font-semibold">Créditos:</span>{" "}
              {card?.credits ? Math.ceil(card?.credits || 0) : "Sob consulta"}
            </span>
            {hasCardUpdateInfo(card) && (
              <span
                className="truncate font-medium"
                title={`Atualizado ${getCardUpdateDisplay(card, "há ").displayText}`}
              >
                <span className="font-semibold">Atualizado</span>{" "}
                {getCardUpdateDisplay(card, "há ").displayText}
              </span>
            )}
          </div>
        </div>
      </div>
      {scoreDetails && (
        <ScoreDetailsModal
          scoreDetails={{
            scoreDetails: card.score ?? [],
            details: card.score_details ?? null,
          }}
          match={Number(card.match_percentage)}
          handleClose={() => setScoreDetails(null)}
        />
      )}
    </TooltipProvider>
  );
});
